import { HttpClient } from '@zeta-core';
import {
  APIResponse,
  ICreateMpsModelPortfolioRequest,
  IGetMPSConfigurationResponse,
  IImportMpsModelPortfolioResponse,
  IMPSConfig,
  IMpsModelPortfolio,
  IMpsModelPortfolioDetail,
  ISaveMPSConfigRequest,
  IUpdateMpsModelPortfolioRequest,
} from '@zeta-types';
import { MPS_SERVICE, FUND_SERVICE } from '../services-path';

const mapSettingsToPortalConfig = (payload?: (Partial<IMPSConfig> & { mpsEnabled?: boolean }) | null): IGetMPSConfigurationResponse => {
  const enableMPS = payload?.enableMPS ?? payload?.mpsEnabled ?? false;
  return {
    MPSConfig: {
      id: payload?.id,
      status: enableMPS ? 'Active' : 'Inactive',
      createdAt: payload?.createdAt || '',
      updatedAt: payload?.updatedAt || '',
      advisorID: payload?.advisorID,
      organisationId: payload?.organisationId,
      enableMPS,
      benchmarkIndex: payload?.benchmarkIndex || '',
      benchmarkDisplayName: payload?.benchmarkDisplayName || '',
      approvalPolicy: payload?.approvalPolicy || 'MANUAL_APPROVAL_REQUIRED',
      clientDriftToleranceBps: payload?.clientDriftToleranceBps ?? 250,
      allowAutoRebalance: payload?.allowAutoRebalance ?? false,
    },
  };
};

export const MpsService = () => {
  return {
    getMPSConfiguration: (organisationId: string): APIResponse<IGetMPSConfigurationResponse> =>
      HttpClient.get<IMPSConfig>(`${MPS_SERVICE}/settings/mps/${encodeURIComponent(organisationId)}`).then((response) => ({
        ...response,
        data: mapSettingsToPortalConfig(response.data),
      })),

    saveMPSConfiguration: (payload: ISaveMPSConfigRequest): APIResponse<IGetMPSConfigurationResponse> =>
      HttpClient.post<IMPSConfig>(`${MPS_SERVICE}/settings/mps`, payload).then((response) => ({
        ...response,
        data: mapSettingsToPortalConfig(response.data),
      })),

    listModelPortfolios: (organisationId: string): APIResponse<IMpsModelPortfolio[]> =>
      HttpClient.get<IMpsModelPortfolio[]>(`${MPS_SERVICE}/model-portfolios?organisationId=${encodeURIComponent(organisationId)}`),

    listRecommendedPortfolios: (organisationId: string) =>
      HttpClient.get<{ portfolios: IMpsModelPortfolioDetail[] }>(
        `${MPS_SERVICE}/recommendation-library?organisationId=${encodeURIComponent(organisationId)}`
      ).then((response) => ({ ...response, data: response.data.portfolios })),
    getModelPortfolioDetail: (portfolioId: string): APIResponse<IMpsModelPortfolioDetail> =>
      HttpClient.get<IMpsModelPortfolioDetail>(`${MPS_SERVICE}/model-portfolios/${encodeURIComponent(portfolioId)}/detail`),

    getDraftPortfolio: (id: string) => HttpClient.get<IMpsModelPortfolioDetail>(`${MPS_SERVICE}/model-portfolios/${id}/draft`),
    publishModelPortfolio: (id: string, versionNumber: number) =>
      HttpClient.post(`${MPS_SERVICE}/model-portfolios/${id}/publish`, { versionNumber, analyseImpactedClients: false }),
    investmentAssets: () => HttpClient.get<FirmInvestmentAsset[]>(`${MPS_SERVICE}/investment-assets`),
    saveInvestmentAsset: (id: string | undefined, revision: number | undefined, content: FirmInvestmentAssetContent) =>
      id
        ? HttpClient.put<FirmInvestmentAsset>(`${MPS_SERVICE}/investment-assets/${id}`, { revision, content })
        : HttpClient.post<FirmInvestmentAsset>(`${MPS_SERVICE}/investment-assets`, { content }),
    publishInvestmentAsset: (id: string, revision: number) =>
      HttpClient.post<FirmInvestmentAsset>(`${MPS_SERVICE}/investment-assets/${id}/publish`, { revision }),
    archiveInvestmentAsset: (id: string, revision: number) =>
      HttpClient.post<FirmInvestmentAsset>(`${MPS_SERVICE}/investment-assets/${id}/archive`, { revision }),
    investmentAssetHistory: (id: string) => HttpClient.get<Array<Record<string, any>>>(`${MPS_SERVICE}/investment-assets/${id}/history`),
    recommendationPolicy: (firm: string) => HttpClient.get<FirmRecommendationPolicy>(`${MPS_SERVICE}/settings/recommendations/${encodeURIComponent(firm)}`),
    saveRecommendationPolicy: (policy: FirmRecommendationPolicy) => HttpClient.put<FirmRecommendationPolicy>(`${MPS_SERVICE}/settings/recommendations`, policy),
    searchInvestmentResearch: (search: string) =>
      HttpClient.get<{ items: Array<Record<string, any>> }>(`${FUND_SERVICE}/funds?status=ACTIVE&size=30&name=${encodeURIComponent(search)}`),
    investmentResearchDetail: (id: string) => HttpClient.get<Record<string, any>>(`${FUND_SERVICE}/funds/${id}`),
    createModelPortfolio: (payload: ICreateMpsModelPortfolioRequest): APIResponse<IMpsModelPortfolio> =>
      HttpClient.post<IMpsModelPortfolio>(`${MPS_SERVICE}/model-portfolios`, payload),

    updateModelPortfolio: (portfolioId: string, payload: IUpdateMpsModelPortfolioRequest, updatedBy: string): APIResponse<IMpsModelPortfolio> =>
      HttpClient.put<IMpsModelPortfolio>(
        `${MPS_SERVICE}/model-portfolios/${encodeURIComponent(portfolioId)}?updatedBy=${encodeURIComponent(updatedBy)}`,
        payload
      ),

    deleteModelPortfolio: (portfolioId: string, updatedBy: string): APIResponse<IMpsModelPortfolio> =>
      HttpClient.delete<IMpsModelPortfolio>(`${MPS_SERVICE}/model-portfolios/${encodeURIComponent(portfolioId)}?updatedBy=${encodeURIComponent(updatedBy)}`),

    importModelPortfolios: (payload: { organisationId: string; uploadedBy: string; file: File }): APIResponse<IImportMpsModelPortfolioResponse> =>
      HttpClient.upload<IImportMpsModelPortfolioResponse>(`${MPS_SERVICE}/model-portfolios/import`, {
        organisationId: payload.organisationId,
        uploadedBy: payload.uploadedBy,
        file: payload.file,
        fileName: payload.file.name,
      }),
  };
};

export type FirmInvestmentAssetContent = {
  name: string;
  identifier: string;
  assetClass: string;
  currency: string;
  source: 'FUND' | 'MANUAL';
  fundId?: string;
  provider?: string;
  riskProfile?: string;
  researchReference?: string;
  targetMarket?: string;
  eligibilityNote?: string;
  restricted?: boolean;
  permissionsReference?: string;
  reviewDate?: string;
  ongoingChargePct?: number | null;
};
export type FirmInvestmentAsset = {
  id: string;
  organisationId: string;
  revision: number;
  publishedRevision?: number;
  status: string;
  content: FirmInvestmentAssetContent;
  hasUnpublishedChanges: boolean;
  available: boolean;
  canPublish: boolean;
};
export type FirmRecommendationPolicy = { organisationId: string; version: number; enabled: boolean; allowExternalFunds: boolean; allowManualAssets: boolean };
export function mpsError(error: unknown): string {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || 'The investment library could not complete this request. Please retry.';
}
